import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Layers, Check, AlertTriangle, Loader2, Volume2 } from "lucide-react";
import { Api, type DebridSource, type ResolvedSource } from "../lib/api.js";

/**
 * Alternate-source picker for Real-Debrid items.
 *
 * The auto-pick in /tv/item/:id probes only the top 3 candidates and takes the
 * first that answers. When that release is broken — dead debrid cache, DMCA
 * placeholder, bad encode, or an audio track nothing can decode — this list is
 * the way out. It applies to both output targets: switching while watching in
 * the browser swaps the <video> src, and switching while pointed at the TV
 * re-casts the raw URL to mpv on the Pi.
 */
export function SourcePicker({
  itemId,
  activeSrcId,
  target,
  bandwidthSaver = false,
  onPick,
}: {
  itemId: string;
  /** src_id currently playing, so it can be marked. */
  activeSrcId: string | null;
  /** Where playback is going — changes the wording, not the list. */
  target: "browser" | "tv";
  /** Re-encode the resolved source to a light 1080p stream (slow connections). */
  bandwidthSaver?: boolean;
  /** Called with the resolved source once the server has probed it. */
  onPick: (resolved: ResolvedSource) => void | Promise<void>;
}): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const [busySrcId, setBusySrcId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);

  const isRd = itemId.startsWith("rd:");

  // Only fetch once the viewer actually opens the list — the lookup hits
  // every configured debrid provider and takes a second or two.
  const { data, isLoading, isError, error: queryError } = useQuery({
    queryKey: ["rd-sources", itemId, target],
    queryFn: () => Api.library.debridSources(itemId, target),
    enabled: isRd && open,
    staleTime: 5 * 60 * 1000,
  });

  if (!isRd) return null;

  async function pick(s: DebridSource): Promise<void> {
    setBusySrcId(s.src_id);
    setError(null);
    setWarning(null);
    try {
      const resolved = await Api.library.debridSource(itemId, s.src_id, bandwidthSaver);
      // The server probes before handing back a URL. An unvalidated result
      // means every provider route for this release is dead — say so instead
      // of loading a player that will just spin or show a placeholder.
      if (!resolved.validated) {
        setWarning(
          `That release didn't respond on ${resolved.providers_tried} provider${
            resolved.providers_tried === 1 ? "" : "s"
          } — it's probably not cached anymore. Try another.`,
        );
        return;
      }
      await onPick(resolved);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusySrcId(null);
    }
  }

  const sources = data?.sources ?? [];

  return (
    <details
      className="card-elev rounded-md"
      open={open}
      onToggle={(e) => setOpen((e.currentTarget as HTMLDetailsElement).open)}
    >
      <summary className="px-3 py-2 text-sm cursor-pointer text-parchment hover:text-ivory inline-flex items-center gap-2">
        <Layers size={14} />
        Other sources
        {sources.length > 0 && (
          <span className="text-xs text-parchment/60">({sources.length})</span>
        )}
      </summary>

      <div className="px-3 pb-3 space-y-2">
        <p className="text-xs text-parchment/60">
          {target === "tv"
            ? "Pick a different release and it will be re-cast to the TV."
            : "Pick a different release if this one is broken or silent."}
          {data && data.total_candidates > sources.length && (
            <>
              {" "}
              {data.total_candidates} candidates, grouped into {sources.length}{" "}
              distinct releases.
            </>
          )}
        </p>

        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-parchment/70">
            <Loader2 size={14} className="animate-spin" /> Searching providers…
          </div>
        )}

        {isError && (
          <div className="text-sm text-amber-400">
            Couldn&apos;t list sources: {(queryError as Error)?.message ?? "unknown error"}
          </div>
        )}

        {!isLoading && !isError && sources.length === 0 && (
          <div className="text-sm text-parchment/70">
            No alternate sources found for this episode.
          </div>
        )}

        {error && <div className="text-sm text-vermilionHi">{error}</div>}
        {warning && <div className="text-sm text-amber-400">{warning}</div>}

        <ul className="space-y-1">
          {sources.map((s: DebridSource) => {
            const isActive = s.src_id === activeSrcId;
            const isBusy = s.src_id === busySrcId;
            return (
              <li key={s.src_id}>
                <button
                  type="button"
                  disabled={isBusy || isActive}
                  onClick={() => void pick(s)}
                  className={`w-full text-left rounded px-2 py-2 text-sm transition ${
                    isActive
                      ? "bg-ivory/10 text-ivory cursor-default"
                      : "hover:bg-ivory/5 text-parchment disabled:opacity-50"
                  }`}
                  title={s.display_title}
                >
                  <span className="flex items-center gap-2">
                    {isBusy ? (
                      <Loader2 size={13} className="animate-spin shrink-0" />
                    ) : isActive ? (
                      <Check size={13} className="shrink-0 text-emerald-400" />
                    ) : (
                      <span className="w-[13px] shrink-0" />
                    )}
                    <span className="font-medium">{s.quality}</span>
                    {s.hdr && <Badge>HDR</Badge>}
                    {s.codec && <Badge>{s.codec}</Badge>}
                    {s.cached === false && (
                      <span
                        className="text-xs text-parchment/45"
                        title="Not in the debrid cache yet. Picking it makes the service fetch the torrent first; until that finishes the stream is a placeholder, not the film."
                      >
                        not cached
                      </span>
                    )}
                    {target === "browser" && s.browser_friendly === false && (
                      <span
                        className="text-xs text-amber-300/90"
                        title="Video in this release is HEVC or AV1. Only audio gets re-encoded — the video is copied through untouched — so a browser without a decoder for it plays sound over a black frame. Fine on the TV."
                      >
                        no browser decode
                      </span>
                    )}
                    {s.size_label && (
                      <span className="text-xs text-parchment/60">{s.size_label}</span>
                    )}
                    {s.seeders !== null && (
                      <span className="text-xs text-parchment/50">👤 {s.seeders}</span>
                    )}
                    {s.alt_providers.length > 0 && (
                      <span
                        className="text-xs text-parchment/40"
                        title={`Also available via ${s.alt_providers.join(", ")} — tried automatically if the first route is dead.`}
                      >
                        +{s.alt_providers.length}
                      </span>
                    )}
                  </span>
                  <span className="block pl-[21px] text-xs text-parchment/50 truncate">
                    {s.display_title}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </details>
  );
}

function Badge({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <span className="text-[10px] uppercase tracking-wide px-1 py-[1px] rounded bg-ivory/10 text-parchment/80">
      {children}
    </span>
  );
}

/** Small chip explaining that audio is being re-encoded for browser playback. */
export function TranscodeChip({ codec }: { codec: string | null }): JSX.Element {
  return (
    <span
      className="inline-flex items-center gap-1 text-xs text-amber-300"
      title={`This release's audio is ${codec ?? "an unsupported codec"}, which browsers can't decode. It's being re-encoded to AAC into an HLS session on the server — seeking and resume both work normally.`}
    >
      <Volume2 size={12} />
      audio re-encoded{codec ? ` (${codec})` : ""}
      <AlertTriangle size={11} className="opacity-70" />
    </span>
  );
}
